import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useSiteCopy } from "../../context/SiteCopyContext";
import { getAtPath } from "../../lib/siteCopyPaths";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string | null;
  children: React.ReactNode;
};

export function ParticipationPanel({ open, onClose, title, subtitle, children }: Props) {
  const { copy } = useSiteCopy();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="participation-panel-backdrop" onClick={onClose} role="presentation">
      <div
        className="participation-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="participation-panel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="participation-panel-header">
          <div>
            <h2 id="participation-panel-title" className="sec-title sec-title-sm">
              {title}
            </h2>
            {subtitle && (
              <p className="participation-panel-sub" style={{ opacity: 0.78, marginTop: 8 }}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            className="participation-panel-close"
            onClick={onClose}
            aria-label={String(getAtPath(copy, "common.close") ?? "Close")}
          >
            ×
          </button>
        </header>
        <div className="participation-panel-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
